import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Info, Layers, ListChecks, Mail, IdCard, Hash } from "lucide-react";
import { IcoStatistiques } from "@/components/crm/CrmIcons";
import CrmShell from "@/components/crm/CrmShell";
import CrmListsSection from "@/components/crm/CrmListsSection";
import GeoListsSection from "@/components/settings/GeoListsSection";
import { LotTypesCatalog } from "@/components/crm/LotTypesManager";
import { TypologiesIcon } from "@/components/crm/CrmConfigIcons";
import { useCrmAdmin } from "@/components/crm/crmShared";
import EmailTemplatesSection from "@/components/settings/EmailTemplatesSection";
import FieldConfigPanel from "@/components/crm/FieldConfig";
import StatsConfigSection from "@/components/crm/stats/StatsConfigSection";
import NumberingSection from "@/components/crm/config/NumberingSection";
import { useAuth } from "@/context/AuthContext";
import { hasRight } from "@/components/crm/crmShared";

// iter361 pt.1 — « Échéanciers » et « Étapes des travaux » vivent dans chaque projet (onglets Échéancier / Avancement des travaux).
const SUBS = [["typologies", "Typologies de lots"], ["listes", "Listes"], ["emails", "Modèles d'emails", "settings.email_templates"], ["champs", "Champs fiche client", "crm.config_champs"], ["numerotation", "Numérotation", "crm.config_champs"], ["statistiques", "Statistiques", "crm.stats_configure"]];
// iter449 pt 1 — icônes Lucide 18 px colorées, aucun fond ; onglet actif souligné 2 px de la couleur de son icône
const SUB_ICONS = { typologies: [Layers, "#2DD4BF"], listes: [ListChecks, "#A78BFA"], emails: [Mail, "#3B9EFF"], champs: [IdCard, "#F59E0B"], numerotation: [Hash, "#F472B6"], statistiques: [IcoStatistiques, "#7F77DD"] };
const CSS = `.cfg-sub{display:inline-flex;align-items:center;gap:9px;font-size:12.5px;padding:11px 16px;color:#c8c8cf;border-bottom:2px solid transparent;margin-bottom:-1px;background:none;cursor:pointer;transition:color .15s,border-color .15s}
.cfg-sub:hover{color:#e8e8ec}.cfg-sub[data-active="true"]{color:#F2F2F5;font-weight:500;border-bottom-color:var(--sub-color)}
.cfg-sub-sep{width:1px;height:24px;background:#26262b;align-self:center;flex-shrink:0}`;

function TypologiesSection() {
  const isAdmin = useCrmAdmin();
  return (
    <div className="grid" style={{ gap: 14 }} data-testid="crm-typologies-section">
      <div className="flex items-center" style={{ background: "#141416", border: "0.5px solid #26262b", borderRadius: 12, padding: "14px 18px", gap: 14 }}>
        <TypologiesIcon size={34} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13.5, fontWeight: 500, color: "#e8e8ec" }}>Typologies de lots</div>
          <div style={{ fontSize: 11.5, color: "#8a8a8f", marginTop: 3 }}>Catalogue de l'organisation (Appartement, Débarras, Place de parking, Local commercial…) — s'applique à tous les blocs et projets.</div>
        </div>
      </div>
      <LotTypesCatalog isAdmin={isAdmin} addLabel="Nouvelle typologie" />
      <div className="flex items-center" style={{ gap: 6, fontSize: 10.5, color: "#71717c" }} data-testid="crm-typologies-note"><Info size={11} />Une typologie utilisée par au moins un lot ne peut pas être supprimée : réaffectez d'abord ses lots.</div>
    </div>
  );
}

/** Commercial › Configuration : sous-onglets Typologies de lots · Listes · Modèles d'emails · Champs fiche client. */
export default function CrmConfig() {
  const [params] = useSearchParams();
  const [sub, setSub] = useState(params.get("sub") || "typologies");
  const { user } = useAuth();
  const subs = SUBS.filter(([, , right]) => !right || hasRight(user, right)); // même droit qu'avant (settings.email_templates), aucun nouveau droit
  return (
    <CrmShell subtitle="Configuration du module"
      fixed={<>
      <div className="flex items-stretch" style={{ borderBottom: "0.5px solid #26262b", marginBottom: 16 }} data-testid="crm-config-subtabs">
        {subs.map(([k, l], i) => {
          const [Icon, color] = SUB_ICONS[k];
          return (
            <span key={k} className="flex items-stretch">
              {i > 0 && <span className="cfg-sub-sep" data-testid="crm-config-sub-sep" />}
              <button type="button" onClick={() => setSub(k)} className="cfg-sub" style={{ "--sub-color": color }} data-testid={`crm-config-sub-${k}`} data-active={sub === k}>
                <Icon size={18} style={{ color, flexShrink: 0 }} />{l}
              </button>
            </span>
          );
        })}
      </div>
      </>}>
      <style>{CSS}</style>
      {sub === "typologies" && <TypologiesSection />}
      {sub === "listes" && <><CrmListsSection /><div style={{ marginTop: 14 }} data-testid="geo-lists-block"><div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#8A8A93", marginBottom: 8 }}>Wilayas, daïras et communes</div><GeoListsSection /></div></>}
      {sub === "emails" && hasRight(user, "settings.email_templates") && <EmailTemplatesSection />}
      {sub === "numerotation" && hasRight(user, "crm.config_champs") && <NumberingSection />}
      {sub === "statistiques" && hasRight(user, "crm.stats_configure") && <StatsConfigSection />}
      {sub === "champs" && hasRight(user, "crm.config_champs") && <div data-testid="crm-config-champs"><FieldConfigPanel inline /></div>}
    </CrmShell>
  );
}
